"use client";

import { useState } from "react";
import { CmsText, cmsAlt, cmsImg } from "@/components/CmsText";
import { AnimatePresence, motion, type Variants } from "motion/react";
import { Reveal } from "@/components/Motion";

type Value = { title: string; points: string[] };

const EASE = [0.16, 1, 0.3, 1] as const;
const listV: Variants = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const rowV: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

// "Our values" — left image, right accordion. Each value expands to reveal its
// bullet points (same dropdown pattern used site-wide), one open at a time.
// The photo and its alt text are editable in place (`<prefix>.image`).
export function ValuesAccordion({
  values,
  c = {},
  cmsPrefix = "values",
  image,
  imageAlt,
}: {
  values: Value[];
  c?: Record<string, string>;
  cmsPrefix?: string;
  image: string;
  imageAlt: string;
}) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <div className="mt-14 grid items-start gap-12 md:grid-cols-[0.95fr_1.05fr] md:gap-16">
      {/* left — image */}
      <Reveal className="md:sticky md:top-28">
        <div className="aspect-[4/5] w-full overflow-hidden rounded-2xl ring-1 ring-brand-line">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={cmsImg(c, `${cmsPrefix}.image`, image)}
            alt={cmsAlt(c, `${cmsPrefix}.image`, imageAlt)}
            data-cms-img={`${cmsPrefix}.image`}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover [filter:contrast(1.03)_saturate(1.04)]"
          />
        </div>
      </Reveal>

      {/* right — values as dropdown accordions; rows reveal with a stagger and
          each panel expands smoothly */}
      <div className="md:pt-2">
        <motion.div
          className="divide-y divide-brand-line border-y border-brand-line"
          variants={listV}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
        >
          {values.map((v, i) => {
            const isOpen = open === i;
            return (
              <motion.div key={i} variants={rowV}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="text-lg font-medium text-brand-ink">
                    <CmsText c={c} k={`${cmsPrefix}.${i}.title`}>{v.title}</CmsText>
                  </span>
                  <span className="text-2xl font-normal leading-none text-brand-accent">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <ul className="space-y-2.5 pb-6 pr-8 text-base font-normal leading-relaxed text-brand-slate">
                        {v.points.map((pt, j) => (
                          <li key={j} className="flex gap-3">
                            <span
                              aria-hidden
                              className="mt-[0.6em] h-1 w-1 shrink-0 rounded-full bg-brand-accent"
                            />
                            <CmsText c={c} k={`${cmsPrefix}.${i}.points.${j}`}>{pt}</CmsText>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}
